// Workforce mode (`/c/{slug}/agents`; was "Agents" until WORKFORCE, 2026-10-26: digital
// employees (DE) and agents (AG, `/agents/ag`, one agent `/agents/ag/{id}` — WF-FIX); workspace-modes
// note §3; M3-08, M4-06; Agents UX pass 2026-10-21; UX pass 2026-10-27). Core for every company:
// the roster and each DE's one page (`/agents/de/{id}`: Overview, Activity, KPIs, Knowledge,
// Setup; `?tab=` names a section). With `agents`: Activity (`/agents/activity`, its Wake-ups state
// was `/agents/wakeups`), runs (`/agents/runs?state=`, `/agents/runs/{id}`, linked from Activity),
// approvals (`/agents/approvals?tab=`, `/agents/approvals/{id}`), rooms (`/agents/rooms`, M4-06) and
// My agent (`/agents/me`, M4-01, opened from the account menu). With the add-on off these screens
// stay reachable and show one banner (off, and who turns it on), never sample rows (founder,
// 2026-10-26). Moved, each old URL redirecting: Budgets (`/agents/budgets…` → Setup › Budgets),
// Skill packs (`/agents/skills…` → Setup › Skill packs for a manager; a member's read-only door
// otherwise), a DE's set-up editor (`/agents/de/{id}/edit` → its Setup section), Connections and
// Webhooks (→ Setup › Integrations). Adding a DE is Setup's "Add digital employee"
// (`/setup/des/new`, the "+" on the sidebar's DIGITAL EMPLOYEES caption). Workforce renders edge
// to edge (app-shell: `main="full"`): the list screens draw their own location header; the
// document-shaped ones sit in `AgentsDoc`.
import { buttonVariants, cn, LocationHeader } from "@companyos/ui";
import {
  createRoute,
  Link,
  Navigate,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  ActivityIcon,
  HandIcon,
  InboxIcon,
  RepeatIcon,
  SendIcon,
  ServerIcon,
  Share2Icon,
  SirenIcon,
} from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import {
  ActivityScreen,
  type ActivityView,
  parseActivityView,
  parseWho,
} from "../features/agents/activity-screen";
import { AgDetailScreen } from "../features/agents/ag-detail-screen";
import { AgRosterScreen } from "../features/agents/ag-roster-screen";
import { AgentsDoc, AgentsOffBanner, LIST_BODY } from "../features/agents/agents-ux";
import { AlertsScreen } from "../features/agents/alerts-screen";
import {
  APPROVAL_KIND_FILTERS,
  APPROVAL_TABS,
  ApprovalDetailScreen,
  type ApprovalKindFilter,
  ApprovalsScreen,
  type ApprovalTab,
} from "../features/agents/approvals-screen";
import { BudgetIncidentsScreen } from "../features/agents/budget-incidents-screen";
import { DelegationScreen } from "../features/agents/delegation-screen";
import { FleetScreen } from "../features/agents/fleet-screen";
import { INTAKE_TABS, IntakeScreen, type IntakeTab } from "../features/agents/intake-screen";
import { LoopsScreen } from "../features/agents/loops-screen";
import { OUTBOX_TABS, OutboxScreen, type OutboxTab } from "../features/agents/outbox-screen";
import { RoomsScreen } from "../features/agents/rooms-screen";
import { RosterScreen } from "../features/agents/roster-screen";
import {
  RUN_FILTERS,
  RunDetailScreen,
  type RunFilterKey,
  RunsScreen,
} from "../features/agents/runs-screen";
import { SkillLibraryScreen } from "../features/agents/skill-library";
import { SkillPackScreen, SkillPacksScreen } from "../features/agents/skill-packs-screen";
import { isManager } from "../features/company";
import {
  CONNECTION_TABS,
  ConnectionsArea,
  type ConnectionTab,
} from "../features/connections/connections-area";
import { MyAgentScreen } from "../features/crew/my-agent";
import { isStepKey, STEP_SECTION } from "../features/de/add-de/edit-de";
import { DE_TABS, DeProfileScreen, type DeTab } from "../features/de/de-profile";
import {
  TEMPLATE_TABS,
  TemplatesScreen,
  type TemplateTab,
} from "../features/templates/templates-screen";
import { useCompany, useFeature } from "../features/use-feature";
import { keepFocus, reconcileHref, validateFocusSearch } from "../focus/focus";
import { NoAccessPage } from "../pages/status-pages";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

export const agentsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "agents",
});

/**
 * A page of the `agents` add-on. Off, it is not hidden: the page's location header and the
 * add-on banner, nothing else (no call to an add-on route, no sample rows; founder 2026-10-26).
 */
function AgentsAddon({
  what,
  list = false,
  children,
}: {
  what:
    | "runs"
    | "approvals"
    | "delegation"
    | "activity"
    | "outbox"
    | "intake"
    | "loops"
    | "alerts"
    | "fleet";
  /** A list body (edge to edge, its own location header), not a document. */
  list?: boolean;
  children: ReactNode;
}) {
  const company = useCompany();
  const agents = useFeature("agents");
  const { title, icon } = OFF_TITLES[what];
  return (
    <ModeGate modeKey="agents">
      {agents.enabled ? (
        list ? (
          children
        ) : (
          <AgentsDoc>{children}</AgentsDoc>
        )
      ) : (
        <div className={LIST_BODY} data-slot="agents-off-screen" data-screen={what}>
          <LocationHeader
            icon={icon}
            title={title}
            {...(what === "activity" ? { actions: <ActivityOffLinks slug={company.slug} /> } : {})}
          />
          <AgentsOffBanner company={company} />
        </div>
      )}
    </ModeGate>
  );
}

/**
 * Codex UX-B r1 #4: with the add-on off, Activity still names its two doors — Runs (its own
 * screen) and Wake-ups (its state) — as the sidebar rows did, each opening its add-on banner.
 */
function ActivityOffLinks({ slug }: { slug: string }) {
  const link = cn(buttonVariants({ variant: "ghost", size: "sm" }));
  return (
    <>
      <Link
        to="/c/$slug/agents/activity"
        params={{ slug }}
        search={(prev: Record<string, unknown>) => ({
          ...keepFocus(prev),
          state: "wakeups" as const,
        })}
        className={link}
      >
        Wake-ups
      </Link>
      <Link to="/c/$slug/agents/runs" params={{ slug }} search={keepFocus} className={link}>
        Runs
      </Link>
    </>
  );
}

const OFF_TITLES = {
  runs: { title: "Runs", icon: ActivityIcon },
  approvals: { title: "Approvals", icon: HandIcon },
  delegation: { title: "Delegation", icon: Share2Icon },
  activity: { title: "Activity", icon: ActivityIcon },
  outbox: { title: "Outbox", icon: SendIcon },
  intake: { title: "Intake", icon: InboxIcon },
  loops: { title: "Loops", icon: RepeatIcon },
  alerts: { title: "Alerts", icon: SirenIcon },
  fleet: { title: "Fleet", icon: ServerIcon },
} as const;

const ID = /^[0-9a-f-]{36}$/i;

export const agentsIndexRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "/",
  component: function Agents() {
    const { api } = agentsIndexRoute.useRouteContext();
    return (
      <ModeGate modeKey="agents">
        <RosterScreen api={api} company={useCompany()} />
      </ModeGate>
    );
  },
});

export const agentsDeRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "de/$id",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((DE_TABS as readonly unknown[]).includes(search.tab) ? { tab: search.tab as DeTab } : {}),
    // HELPERS: the helper the Helpers tab scrolls to and marks.
    ...(typeof search.helper === "string" && /^[0-9a-f-]{36}$/i.test(search.helper)
      ? { helper: search.helper }
      : {}),
  }),
  // The page writes the playhead: the Chart then opens this DE's seat.
  beforeLoad: ({ params, location }) => {
    const href = reconcileHref(location, { subject: { kind: "de", id: params.id } });
    if (href) throw redirect({ href, replace: true });
  },
  component: function AgentsDe() {
    const { api } = agentsDeRoute.useRouteContext();
    const { id } = agentsDeRoute.useParams();
    const { tab, helper } = agentsDeRoute.useSearch();
    const navigate = useNavigate();
    return (
      <ModeGate modeKey="agents">
        <DeProfileScreen
          api={api}
          company={useCompany()}
          id={id}
          tab={tab}
          helper={helper}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }),
            } as never)
          }
        />
      </ModeGate>
    );
  },
});

// DE-WIZARD (review #12) made `/agents/de/{id}/edit?step=` a second editor of a DE's set-up; UX pass
// 1.2 folded its steps into the profile's Setup sections (one editor). The old URL redirects to
// the section its step names (`STEP_SECTION`; the job by default), the rest of the query kept.
export const agentsDeEditRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "de/$id/edit",
  beforeLoad: ({ params, search }) => {
    const { step, ...rest } = search as Record<string, unknown>;
    throw redirect({
      to: "/c/$slug/agents/de/$id",
      params,
      search: { ...rest, tab: isStepKey(step) ? STEP_SECTION[step] : "job" },
      replace: true,
    } as never);
  },
});

/** WORKFORCE: the agents (AG) — core, every member sees them. */
export const agentsAgRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "ag",
  component: function AgentsAg() {
    const { api } = agentsAgRoute.useRouteContext();
    return (
      <ModeGate modeKey="agents">
        <AgRosterScreen api={api} company={useCompany()} />
      </ModeGate>
    );
  },
});

/** WF-FIX: one agent (AG) on its own page — core, every member opens it. */
export const agentsAgItemRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "ag/$id",
  validateSearch: validateFocusSearch,
  beforeLoad: ({ params }) => {
    if (!ID.test(params.id)) throw redirect({ to: "/c/$slug/agents/ag", params, replace: true });
  },
  component: function AgentsAgItem() {
    const { api } = agentsAgItemRoute.useRouteContext();
    const { id } = agentsAgItemRoute.useParams();
    return (
      <ModeGate modeKey="agents">
        <AgDetailScreen api={api} company={useCompany()} id={id} />
      </ModeGate>
    );
  },
});

// M4-02: the skill packs the company's DEs may run (behind `agents`; the screen says so when off).
// UX pass 1.1: their home is Setup › Workforce › Skill packs; a manager is redirected there (search
// kept). A member cannot open Setup, so for everyone else this URL stays the read-only door the
// DE profile's Skills links to (no sidebar lists it).
export const agentsSkillsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "skills",
  component: function AgentsSkills() {
    const { api } = agentsSkillsRoute.useRouteContext();
    const company = useCompany();
    if (isManager(company))
      return <MovedToSetup to="/c/$slug/setup/skill-packs" params={{ slug: company.slug }} />;
    return (
      <ModeGate modeKey="agents">
        <AgentsDoc>
          <SkillPacksScreen api={api} company={company} />
        </AgentsDoc>
      </ModeGate>
    );
  },
});

export const agentsSkillRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "skills/$id",
  component: function AgentsSkill() {
    const { api } = agentsSkillRoute.useRouteContext();
    const { id } = agentsSkillRoute.useParams();
    const company = useCompany();
    if (isManager(company))
      return (
        <MovedToSetup to="/c/$slug/setup/skill-packs/$id" params={{ slug: company.slug, id }} />
      );
    return (
      <ModeGate modeKey="agents">
        <AgentsDoc>
          <SkillPackScreen api={api} company={company} id={id} />
        </AgentsDoc>
      </ModeGate>
    );
  },
});

/**
 * HELPERS: Workforce › Skills, the skill library (each skill once, "Used by" its DEs and helpers;
 * `?skill=` marks one). Core: every member reads it, like the helpers.
 */
export const agentsSkillLibraryRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "skill-library",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.skill === "string" && search.skill.length <= 120
      ? { skill: search.skill }
      : {}),
  }),
  component: function AgentsSkillLibrary() {
    const { api } = agentsSkillLibraryRoute.useRouteContext();
    const { skill } = agentsSkillLibraryRoute.useSearch();
    return (
      <ModeGate modeKey="agents">
        <SkillLibraryScreen api={api} company={useCompany()} skill={skill} />
      </ModeGate>
    );
  },
});

/** A manager's redirect to a page's new Setup home, the query (focus) kept, history replaced. */
function MovedToSetup({ to, params }: { to: string; params: Record<string, string> }) {
  const search = useLocation({ select: (l) => l.search as Record<string, unknown> });
  // `to` is one of the two Setup routes; the generic Navigate props are too wide to name here.
  const props = { to, params, search, replace: true } as unknown as ComponentProps<typeof Navigate>;
  return <Navigate {...props} />;
}

/** My agent (M4-01, M4-06): the signed-in person's own crew; the add-on banner alone when off. */
export const agentsMeRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "me",
  component: function AgentsMe() {
    const { api } = agentsMeRoute.useRouteContext();
    return (
      <ModeGate modeKey="agents">
        <MyAgentScreen api={api} company={useCompany()} />
      </ModeGate>
    );
  },
});

/** Rooms (M4-06): the agent rooms, whose turn it is, take over and release. */
export const agentsRoomsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "rooms",
  validateSearch: validateFocusSearch,
  component: function AgentsRooms() {
    const { api } = agentsRoomsRoute.useRouteContext();
    return (
      <ModeGate modeKey="agents">
        <RoomsScreen api={api} company={useCompany()} />
      </ModeGate>
    );
  },
});

/**
 * TEMPLATES-T2..T4: Templates (`/agents/templates?tab=roles|overlay&role=`): the role templates a
 * DE is deployed from and the company's overlay. Managers (the API's rule); the open role's
 * preview drawer is in the URL.
 */
export const agentsTemplatesRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "templates",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(TEMPLATE_TABS.includes(search.tab as TemplateTab) && search.tab !== "roles"
      ? { tab: search.tab as TemplateTab }
      : {}),
    ...(typeof search.role === "string" && ID.test(search.role) ? { role: search.role } : {}),
  }),
  component: function AgentsTemplates() {
    const { api } = agentsTemplatesRoute.useRouteContext();
    const search = agentsTemplatesRoute.useSearch();
    const company = useCompany();
    const navigate = useNavigate();
    if (!isManager(company)) return <NoAccessPage what="Templates" slug={company.slug} />;
    const go = (patch: { tab?: TemplateTab | undefined; role?: string | undefined }) =>
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...patch };
          for (const k of ["tab", "role"]) if (next[k] === undefined) delete next[k];
          return next;
        },
      } as never);
    return (
      <ModeGate modeKey="agents">
        <TemplatesScreen
          api={api}
          company={company}
          tab={search.tab ?? "roles"}
          onTab={(t) => go({ tab: t === "roles" ? undefined : t })}
          roleId={search.role}
          onRole={(id) => go({ role: id ?? undefined })}
        />
      </ModeGate>
    );
  },
});

export const agentsRunsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "runs",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((RUN_FILTERS as readonly unknown[]).includes(search.state) && search.state !== "all"
      ? { state: search.state as RunFilterKey }
      : {}),
  }),
  component: function AgentsRuns() {
    const { api } = agentsRunsRoute.useRouteContext();
    const { state } = agentsRunsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <AgentsAddon what="runs">
        <RunsScreen
          api={api}
          company={useCompany()}
          filter={state ?? "all"}
          onFilter={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { state: _drop, ...rest } = prev;
                return next === "all" ? rest : { ...rest, state: next };
              },
            } as never)
          }
        />
      </AgentsAddon>
    );
  },
});

/**
 * ACTIVITY (design 2026-10-27): what the workforce is doing, a feed of runs (`?who=de:<id>`,
 * `ag:<id>`, `team:<id>`; `?state=live|waiting|done|failed`), and (UX pass 1.1) the wake-ups
 * (`?state=wakeups`, was `/agents/wakeups`). Runs and its route stay, linked from Activity's
 * header, until Andrei decides on the swap.
 */
export const agentsActivityRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "activity",
  validateSearch: (search: Record<string, unknown>) => {
    const who = parseWho(search.who);
    const state = parseActivityView(search.state);
    return {
      ...validateFocusSearch(search),
      ...(who ? { who: `${who.kind}:${who.id}` } : {}),
      ...(state ? { state: state as ActivityView } : {}),
    };
  },
  component: function AgentsActivity() {
    const { api } = agentsActivityRoute.useRouteContext();
    const { who, state } = agentsActivityRoute.useSearch();
    const navigate = useNavigate();
    const parsed = parseWho(who);
    return (
      <AgentsAddon what="activity" list>
        <ActivityScreen
          api={api}
          company={useCompany()}
          filter={{ ...(parsed ? { who: parsed } : {}), ...(state ? { state } : {}) }}
          onFilter={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { who: _w, state: _s, ...rest } = prev;
                return {
                  ...rest,
                  ...(next.who ? { who: `${next.who.kind}:${next.who.id}` } : {}),
                  ...(next.state ? { state: next.state } : {}),
                };
              },
            } as never)
          }
        />
      </AgentsAddon>
    );
  },
});

/**
 * LOOP-BREAKER: the runs the breaker stopped as loops, what each burned, and Retry / Reassign /
 * Close (`/agents/loops`, linked from Activity's header and the Team sidebar).
 */
export const agentsLoopsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "loops",
  validateSearch: validateFocusSearch,
  component: function AgentsLoops() {
    const { api } = agentsLoopsRoute.useRouteContext();
    return (
      <AgentsAddon what="loops" list>
        <LoopsScreen api={api} company={useCompany()} />
      </AgentsAddon>
    );
  },
});

/**
 * MONITORING-P1: Team › Alerts (`/agents/alerts`): stuck runs, post storms, stalled job stages and
 * tripped guards, with Ack and Resolve. Managers (the API's rule).
 */
export const agentsAlertsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "alerts",
  validateSearch: validateFocusSearch,
  component: function AgentsAlerts() {
    const { api } = agentsAlertsRoute.useRouteContext();
    const company = useCompany();
    if (!isManager(company)) return <NoAccessPage what="Alerts" slug={company.slug} />;
    return (
      <AgentsAddon what="alerts" list>
        <AlertsScreen api={api} company={company} />
      </AgentsAddon>
    );
  },
});

/**
 * MONITORING-P2: Team › Fleet (`/agents/fleet`): per DE seat, where its bridge runs, the bridge
 * version, the last heartbeat and event, telemetry on/off and open alerts. Managers.
 */
export const agentsFleetRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "fleet",
  validateSearch: validateFocusSearch,
  component: function AgentsFleet() {
    const { api } = agentsFleetRoute.useRouteContext();
    const company = useCompany();
    if (!isManager(company)) return <NoAccessPage what="Fleet" slug={company.slug} />;
    return (
      <AgentsAddon what="fleet" list>
        <FleetScreen api={api} company={company} />
      </AgentsAddon>
    );
  },
});

/**
 * MONITORING-P2: Team › Budget incidents (`/agents/budget-incidents?de=&period=`), linked from
 * the seat drawer's Finance links and Team › Alerts. Managers; budgets are core (no add-on gate).
 */
export const agentsBudgetIncidentsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "budget-incidents",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.de === "string" && ID.test(search.de) ? { de: search.de } : {}),
    ...(typeof search.period === "string" && /^[0-9A-Za-z-]{1,32}$/.test(search.period)
      ? { period: search.period }
      : {}),
  }),
  component: function AgentsBudgetIncidents() {
    const { api } = agentsBudgetIncidentsRoute.useRouteContext();
    const { de, period } = agentsBudgetIncidentsRoute.useSearch();
    const navigate = useNavigate();
    const company = useCompany();
    if (!isManager(company)) return <NoAccessPage what="Budget incidents" slug={company.slug} />;
    return (
      <ModeGate modeKey="agents">
        <BudgetIncidentsScreen
          api={api}
          company={company}
          de={de}
          period={period}
          onFilter={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { de: _d, period: _p, ...rest } = prev;
                return {
                  ...rest,
                  ...(next.de ? { de: next.de } : {}),
                  ...(next.period ? { period: next.period } : {}),
                };
              },
            } as never)
          }
        />
      </ModeGate>
    );
  },
});

export const agentsRunRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "runs/$id",
  validateSearch: validateFocusSearch,
  component: function AgentsRun() {
    const { api } = agentsRunRoute.useRouteContext();
    const { id } = agentsRunRoute.useParams();
    return (
      <AgentsAddon what="runs">
        <RunDetailScreen api={api} company={useCompany()} id={id} />
      </AgentsAddon>
    );
  },
});

export const agentsApprovalsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "approvals",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((APPROVAL_TABS as readonly unknown[]).includes(search.tab) && search.tab !== "mine"
      ? { tab: search.tab as ApprovalTab }
      : {}),
    // AGENT-REPORTING: `?kind=kpi_value` (the KPI reviews).
    ...((APPROVAL_KIND_FILTERS as readonly unknown[]).includes(search.kind)
      ? { kind: search.kind as ApprovalKindFilter }
      : {}),
  }),
  component: function AgentsApprovals() {
    const { api } = agentsApprovalsRoute.useRouteContext();
    const { tab, kind } = agentsApprovalsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <AgentsAddon what="approvals">
        <ApprovalsScreen
          api={api}
          company={useCompany()}
          tab={tab ?? "mine"}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { tab: _drop, ...rest } = prev;
                return next === "mine" ? rest : { ...rest, tab: next };
              },
            } as never)
          }
          kind={kind}
          onKind={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { kind: _drop, ...rest } = prev;
                return next ? { ...rest, kind: next } : rest;
              },
            } as never)
          }
        />
      </AgentsAddon>
    );
  },
});

export const agentsApprovalRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "approvals/$id",
  validateSearch: validateFocusSearch,
  component: function AgentsApproval() {
    const { api } = agentsApprovalRoute.useRouteContext();
    const { id } = agentsApprovalRoute.useParams();
    return (
      <AgentsAddon what="approvals">
        <ApprovalDetailScreen api={api} company={useCompany()} id={id} />
      </AgentsAddon>
    );
  },
});

/** STEP1-OUTBOX: Workforce › Outbox (`?tab=pending|sent|failed`). */
export const agentsOutboxRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "outbox",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((OUTBOX_TABS as readonly unknown[]).includes(search.tab) && search.tab !== "all"
      ? { tab: search.tab as OutboxTab }
      : {}),
    // STEP1-OUTBOX: the row an inbox notice points at (highlighted)
    ...(typeof search.item === "string" && /^[0-9a-f-]{36}$/i.test(search.item)
      ? { item: search.item }
      : {}),
  }),
  component: function AgentsOutbox() {
    const { api } = agentsOutboxRoute.useRouteContext();
    const { tab, item } = agentsOutboxRoute.useSearch();
    const navigate = useNavigate();
    return (
      <AgentsAddon what="outbox">
        <OutboxScreen
          api={api}
          company={useCompany()}
          tab={tab ?? "all"}
          {...(item ? { item } : {})}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { tab: _drop, ...rest } = prev;
                return next === "all" ? rest : { ...rest, tab: next };
              },
            } as never)
          }
        />
      </AgentsAddon>
    );
  },
});

/** INC2-INTAKE: Workforce › Intake (`?tab=dead` for the dead letters). */
export const agentsIntakeRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "intake",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((INTAKE_TABS as readonly unknown[]).includes(search.tab) && search.tab !== "events"
      ? { tab: search.tab as IntakeTab }
      : {}),
  }),
  component: function AgentsIntake() {
    const { api } = agentsIntakeRoute.useRouteContext();
    const { tab } = agentsIntakeRoute.useSearch();
    const navigate = useNavigate();
    return (
      <AgentsAddon what="intake">
        <IntakeScreen
          api={api}
          company={useCompany()}
          tab={tab ?? "events"}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { tab: _drop, ...rest } = prev;
                return next === "events" ? rest : { ...rest, tab: next };
              },
            } as never)
          }
        />
      </AgentsAddon>
    );
  },
});

export const agentsDelegationRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "delegations/$id",
  validateSearch: validateFocusSearch,
  component: function AgentsDelegation() {
    const { api } = agentsDelegationRoute.useRouteContext();
    const { id } = agentsDelegationRoute.useParams();
    return (
      <AgentsAddon what="delegation">
        <DelegationScreen api={api} company={useCompany()} id={id} />
      </AgentsAddon>
    );
  },
});

/**
 * UX pass 1.1: Wake-ups folded into Activity as its "Wake-ups" state (what waits to run, and what
 * admission did with each wake-up). The old URL redirects there, the query (focus) kept.
 */
export const agentsWakeupsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "wakeups",
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/c/$slug/agents/activity",
      params,
      search: { ...(search as Record<string, unknown>), state: "wakeups" },
      replace: true,
    } as never);
  },
});

/**
 * UX pass 1.1: Budgets moved to Setup › Workforce (managers only, as here). The old URLs redirect,
 * the query (focus) kept.
 */
export const agentsBudgetsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "budgets",
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: "/c/$slug/setup/budgets", params, search, replace: true });
  },
});

export const agentsBudgetRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "budgets/$id",
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: "/c/$slug/setup/budgets/$id", params, search, replace: true });
  },
});

/**
 * CONNECTIONS-AREA (2026-10-30): Team › Connections, one place for every outside login (tabs in
 * `?tab=`, `?whose=mine` for the caller's own; `scope` is the focus's key). This URL was the old
 * Agents › Connections (a
 * redirect to Setup's Connection checks); it is the area again. Setup › Keys & connections and
 * `/team/connections` open it. Core: a company without the `agents` add-on keeps its keys and
 * sign-ins here (the Accounts tab says saved logins come with the add-on).
 */
export const agentsConnectionsRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "connections",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((CONNECTION_TABS as readonly unknown[]).includes(search.tab) && search.tab !== "apps"
      ? { tab: search.tab as ConnectionTab }
      : {}),
    ...(search.whose === "mine" ? { whose: "mine" as const } : {}),
  }),
  component: function AgentsConnections() {
    const { api } = agentsConnectionsRoute.useRouteContext();
    const { tab, whose } = agentsConnectionsRoute.useSearch();
    const company = useCompany();
    const navigate = useNavigate();
    const set = (key: "tab" | "whose", value: string | null) =>
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => {
          const { [key]: _drop, ...rest } = prev;
          return value ? { ...rest, [key]: value } : rest;
        },
      } as never);
    return (
      <ModeGate modeKey="agents">
        <ConnectionsArea
          api={api}
          company={company}
          {...(tab ? { tab } : {})}
          {...(whose === "mine" ? { scope: "mine" as const } : {})}
          onTab={(t) => set("tab", t === "apps" ? null : t)}
          onScope={(s) => set("whose", s === "mine" ? s : null)}
          onLogIn={(a) =>
            void navigate({
              to: "/c/$slug/live",
              params: { slug: company.slug },
              search: { tab: "accounts", login: a.id },
            } as never)
          }
        />
      </ModeGate>
    );
  },
});

/** CONNECTIONS-AREA: the design's URL for Team › Connections; opens it, the query kept. */
export const teamConnectionsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "team/connections",
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: "/c/$slug/agents/connections", params, search, replace: true });
  },
});

export const agentsWebhooksRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "webhooks",
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: "/c/$slug/setup/webhooks", params, search, replace: true });
  },
});

export const agentsRoutes = [
  agentsIndexRoute,
  agentsAgRoute,
  agentsAgItemRoute,
  agentsMeRoute,
  agentsDeRoute,
  agentsDeEditRoute,
  agentsSkillsRoute,
  agentsSkillRoute,
  agentsSkillLibraryRoute,
  agentsRoomsRoute,
  agentsTemplatesRoute,
  agentsActivityRoute,
  agentsLoopsRoute,
  agentsAlertsRoute,
  agentsFleetRoute,
  agentsBudgetIncidentsRoute,
  agentsRunsRoute,
  agentsRunRoute,
  agentsApprovalsRoute,
  agentsApprovalRoute,
  agentsOutboxRoute,
  agentsIntakeRoute,
  agentsDelegationRoute,
  agentsWakeupsRoute,
  agentsBudgetsRoute,
  agentsBudgetRoute,
  agentsConnectionsRoute,
  agentsWebhooksRoute,
] as const;
